import { useMutation } from "@apollo/client/react";
import {
  ContentStatus,
  SimulationCaseById,
  UpdateSimulationCase,
  UpdateSimulationCaseVariables,
} from "__generated__/types";
import deleteIcon from "common/assets/font-awesome/delete.svg";
import { Button, CancelOrSubmit, IconButton } from "common/buttons";
import { MutationLoadingOrError } from "common/components/forms/MutationLoadingOrError";
import { TextField } from "common/components/forms/TextField";
import { AdministrationLayout } from "common/components/layouts/AdministrationLayout";
import { LoadingLayout } from "common/components/layouts/LoadingLayout";
import { NoDataError } from "common/components/NoDataError";
import { Paragraph } from "common/components/Typography";
import { usePreviousPath } from "common/context/PreviousPathContext";
import { useSimpleQuery } from "common/graphql/useSimpleQuery";
import { urls } from "common/urls";
import { MUTATION, QUERY } from "domain/common/simulation-mutations";
import { simulationBreadcrumb } from "domain/pages/breadcrumbs";
import React, { useEffect, useState } from "react";
import { FormProvider, useForm, UseFormReturn } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { removeTypename } from "../util";
import styles from "./SimulationAdmin.module.css";

interface FormValues {
  diagnosis: Outcome[];
  treatment: Outcome[];
  referral: Outcome[];
}

interface Outcome {
  possibleOutcome: string;
  isCorrectOutcome: boolean;
}

type RouteProps = {
  simulationCaseId: string;
};

function updateList(
  list: Outcome[],
  listName: string,
  methods: UseFormReturn<FormValues>,
): any {
  return list.map((item, index) => ({
    //@ts-ignore
    possibleOutcome: methods.getValues(`${listName}.${index}.possibleOutcome`),
    isCorrectOutcome: item.isCorrectOutcome,
  }));
}

export const SimulationDiagnosisAdminPage: React.FC = () => {
  const methods = useForm<FormValues>({
    defaultValues: {
      diagnosis: [],
      referral: [],
      treatment: [],
    },
  });

  const navigate = useNavigate();
  const previousPath = usePreviousPath();
  const { simulationCaseId } = useParams<RouteProps>();

  const [diagnoseList, setDiagnoseList] = useState<Outcome[]>([]);
  const [treatmentList, setTreatmentList] = useState<Outcome[]>([]);
  const [referralList, setReferralList] = useState<Outcome[]>([]);

  const { data, loadingOrError, refetch } = useSimpleQuery<SimulationCaseById>(
    QUERY,
    {
      variables: { id: simulationCaseId },
    },
  );

  const [mutate, { error, loading }] = useMutation<
    UpdateSimulationCase,
    UpdateSimulationCaseVariables
  >(MUTATION);

  useEffect(() => {
    if (data && data.simulationCaseById) {
      setDiagnoseList((data.simulationCaseById.diagnosis as Outcome[]) ?? []);
      setTreatmentList((data.simulationCaseById.treatment as Outcome[]) ?? []);
      setReferralList((data.simulationCaseById.referral as Outcome[]) ?? []);
    }
  }, [data]);

  if (!simulationCaseId) {
    throw new Error("Unexpected error: Missing simulationCaseId");
  }

  if (loadingOrError) {
    return <LoadingLayout>{loadingOrError}</LoadingLayout>;
  }

  if (!data || !data.simulationCaseById) {
    return (
      <LoadingLayout>
        <NoDataError />
      </LoadingLayout>
    );
  }

  const {
    title,
    patientInformation,
    module,
    difficulty,
    clinicalCategory,
    assessments,
    status,
    instantReferral,
    nextCase,
  } = data.simulationCaseById;

  const onSubmit = async () => {
    const updatedDiagnosis = updateList(diagnoseList, "diagnosis", methods);
    const updatedTreatment = updateList(treatmentList, "treatment", methods);
    const updatedReferral = updateList(referralList, "referral", methods);

    const result = await mutate({
      variables: {
        input: {
          id: simulationCaseId,
          simulationCaseInput: {
            title: title ?? "",
            patientInformation: removeTypename(patientInformation),
            status: status ?? ContentStatus.DRAFT,
            module: module ?? "",
            difficulty: difficulty ?? "",
            clinicalCategory: clinicalCategory ?? "",
            assessments: assessments?.map(removeTypename),
            diagnosis: updatedDiagnosis.map(removeTypename),
            treatment: updatedTreatment.map(removeTypename),
            referral: updatedReferral.map(removeTypename),
            instantReferral: removeTypename(instantReferral) ?? {
              referralValue: false,
              referralContent: "",
            },
            nextCase: removeTypename(nextCase),
          },
        },
      },
    });

    if (result.data) {
      exitEditPage();
      void refetch();
    }
  };

  const exitEditPage = () => {
    if (previousPath && !previousPath.includes("/admin")) {
      navigate(-1);
    } else {
      navigate(urls.simulationAdminOverview);
    }
  };

  const handleNewItem = (
    setList: React.Dispatch<React.SetStateAction<Outcome[]>>,
  ) => {
    setList((prevList) => [
      ...prevList,
      { possibleOutcome: "", isCorrectOutcome: true },
    ]);
  };

  const handleRemoveItem = (
    setList: React.Dispatch<React.SetStateAction<Outcome[]>>,
    index: number,
  ) => {
    setList((prevList) => prevList.filter((_, i) => i !== index));
  };

  const handleToggleOutcome = (
    setList: React.Dispatch<React.SetStateAction<Outcome[]>>,
    index: number,
  ) => {
    setList((prevList) =>
      prevList.map((item, i) =>
        i === index
          ? { ...item, isCorrectOutcome: !item.isCorrectOutcome }
          : item,
      ),
    );
  };

  return (
    <AdministrationLayout
      title={title ?? "Simulering"}
      breadcrumbItems={simulationBreadcrumb}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <div className={styles.headerContainer}>
            <h3 className={styles.header}>Sett diagnose:</h3>
            <ul className={styles.quizContainer}>
              {diagnoseList.length ? (
                diagnoseList.map((test, index) => (
                  <React.Fragment key={index}>
                    <div className={styles.outcomeOption}>
                      <input
                        type="checkbox"
                        checked={test.isCorrectOutcome}
                        onChange={() => {
                          handleToggleOutcome(setDiagnoseList, index);
                        }}
                        className={styles.checkbox}
                      />
                      <div className={styles.textField}>
                        <TextField
                          title={`Diagnose ${index + 1}`}
                          fieldProps={methods.register(
                            `diagnosis.${index}.possibleOutcome`,
                            {
                              required: "Du må gi alternativet en tittel",
                            },
                          )}
                          defaultValue={test.possibleOutcome}
                        />
                      </div>
                      <IconButton
                        icon={deleteIcon}
                        altText={`Slett undersøkelsen med navn ${test.possibleOutcome}`}
                        onClick={() => {
                          handleRemoveItem(setDiagnoseList, index);
                        }}
                        className={styles.iconButton}
                      />
                    </div>
                  </React.Fragment>
                ))
              ) : (
                <Paragraph>Ingen diagnoser eksisterer, lag nye.</Paragraph>
              )}
            </ul>
            <Button
              type="button"
              className={styles.addButton}
              onClick={() => {
                handleNewItem(setDiagnoseList);
              }}
            >
              Legg til diagnose
            </Button>

            <h3 className={styles.header}>Velg behandling:</h3>
            <ul className={styles.quizContainer}>
              {treatmentList.length ? (
                treatmentList.map((test, index) => (
                  <React.Fragment key={index}>
                    <div className={styles.outcomeOption}>
                      <input
                        type="checkbox"
                        checked={test.isCorrectOutcome}
                        onChange={() => {
                          handleToggleOutcome(setTreatmentList, index);
                        }}
                        className={styles.checkbox}
                      />
                      <div className={styles.textField}>
                        <TextField
                          title={`Behandling ${index + 1}`}
                          fieldProps={methods.register(
                            `treatment.${index}.possibleOutcome`,
                            {
                              required: "Du må gi alternativet en tittel",
                            },
                          )}
                          defaultValue={test.possibleOutcome}
                        />
                      </div>
                      <IconButton
                        icon={deleteIcon}
                        altText={`Slett behandlingen med navn ${test.possibleOutcome}`}
                        onClick={() => {
                          handleRemoveItem(setTreatmentList, index);
                        }}
                        className={styles.iconButton}
                      />
                    </div>
                  </React.Fragment>
                ))
              ) : (
                <Paragraph>Ingen behandlinger eksisterer, lag nye.</Paragraph>
              )}
            </ul>
            <Button
              type="button"
              className={styles.addButton}
              onClick={() => {
                handleNewItem(setTreatmentList);
              }}
            >
              Legg til behandling
            </Button>

            <h3 className={styles.header}>Vurder viderehenvisning:</h3>
            <ul className={styles.quizContainer}>
              {referralList.length ? (
                referralList.map((test, index) => (
                  <React.Fragment key={index}>
                    <div className={styles.outcomeOption}>
                      <input
                        type="checkbox"
                        checked={test.isCorrectOutcome}
                        onChange={() => {
                          handleToggleOutcome(setReferralList, index);
                        }}
                        className={styles.checkbox}
                      />
                      <div className={styles.textField}>
                        <TextField
                          title={`Viderehenvisning ${index + 1}`}
                          fieldProps={methods.register(
                            `referral.${index}.possibleOutcome`,
                            {
                              required: "Du må gi alternativet en tittel",
                            },
                          )}
                          defaultValue={test.possibleOutcome}
                        />
                      </div>
                      <IconButton
                        icon={deleteIcon}
                        altText={`Slett viderehenvisningen med navn ${test.possibleOutcome}`}
                        onClick={() => {
                          handleRemoveItem(setReferralList, index);
                        }}
                        className={styles.iconButton}
                      />
                    </div>
                  </React.Fragment>
                ))
              ) : (
                <Paragraph>
                  Ingen viderehenvisninger eksisterer, lag nye.
                </Paragraph>
              )}
            </ul>
            <Button
              type="button"
              className={styles.addButton}
              onClick={() => {
                handleNewItem(setReferralList);
              }}
            >
              Legg til viderehenvisning
            </Button>
          </div>

          <MutationLoadingOrError loading={loading} error={error} />

          <CancelOrSubmit
            onCancel={exitEditPage}
            isLoading={loading}
            submitText="Lagre endringer"
          />
        </form>
      </FormProvider>
    </AdministrationLayout>
  );
};
